import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleCallback } from "@fmc-digital/core-auth";
import { Text } from "@fmc-digital/ui";
import { handlePostCallbackRedirect, performRedirect } from "@/utils";

/**
 * Callback Page
 * Handles OAuth callback from Okta and SSO session establishment
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function processCallback() {
      try {
        // Check if sessionToken is in URL (SSO from another domain)
        const urlParams = new URLSearchParams(window.location.search);
        const sessionToken = urlParams.get('sessionToken');
        
        if (sessionToken) {
          // Store the Okta sessionToken for SSO
          sessionStorage.setItem('okta_session_token', sessionToken);
          performRedirect("/success");
          return;
        }
        
        // Normal OAuth callback flow
        const tokens = await handleCallback();

        if (tokens) {
          handlePostCallbackRedirect();
        } else {
          setError("Authentication failed. No tokens received.");
        }
      } catch (err) {
        console.error("Callback error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    }

    processCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="auth-card text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-error"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>

        <Text variant="headlineSmall" className="mb-2">
          Authentication Failed
        </Text>
        <Text variant="bodyMedium" color="error" className="mb-6">
          {error}
        </Text>

        <a href="/login" className="auth-link">
          Return to Login
        </a>
      </div>
    );
  }

  return (
    <div className="w-full flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-6" />
        <Text variant="bodyLarge">Completing authentication...</Text>
      </div>
    </div>
  );
}
